import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';

import { environment } from '../../../environments/environment';
import { Session } from '../interfaces';
import { Entity, Capacities } from '../models';
import { SaleConfig } from '../configs/sale.config';

interface HierarchiesTem {
  dispatchEnabled: boolean;
  hierarchyCode: string;
  pickingEnabled: boolean;
  retireEnabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EntityService {
  constructor(private apollo: Apollo) { }

  /**
   * Función que retorna lista de entidades
   * @param type Tipo de entidad
   * @param enabled estado de la entidad
   */
  queryEntity(type, enabled) {
    return this.apollo
    .query({
      query: gql`
        query getEntities(
          $limit: Int
          $offset: Int
          $entityType: EntityType
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            entityType: $entityType
            enabled: $enabled
          ) {
            entityCode
            entityType
            entityName
            entityAddress
            entityTypeDescription
            ubigeoCode
            active
            managingEntities
            hierarchyCode
            ubigeo {
              ubigeoCode
              district
              province
              department
            }
          }
        }
      `,
      variables: {
        entityType: type,
        enabled: enabled
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });
  }

  queryEntityMultiCompany(type, enabled, isMultiCompany) {
    return this.apollo
    .query({
      query: gql`
        query getEntities(
          $limit: Int
          $offset: Int
          $entityType: EntityType
          $enabled: Boolean
          $isMultiCompany: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            entityType: $entityType
            enabled: $enabled
            isMultiCompany: $isMultiCompany
          ) {
            companyCode
            entityCode
            entityType
            entityName
            entityAddress
            entityTypeDescription
            ubigeoCode
            active
            managingEntities
            hierarchyCode
            ubigeo {
              ubigeoCode
              district
              province
              department
            }
          }
        }
      `,
      variables: {
        entityType: type,
        enabled: enabled,
        isMultiCompany: isMultiCompany
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });
  }

  listEntity(type, enabled, onlyCompany: boolean = false, companyCodeInput: string = null) {
    const entityType = type ? type : 'TD';
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (session && session['entity' + entityType] && !onlyCompany) {
      return of(session['entity' + entityType]);
    } else {
      return this.apollo
      .query({
        query: gql`
          query listEntities(
            $limit: Int
            $offset: Int
            $entityType: EntityType
            $enabled: Boolean,
            $companyCodeInput: String
          ) {
            listEntities(
              limit: $limit
              offset: $offset
              entityType: $entityType
              enabled: $enabled,
              companyCodeInput: $companyCodeInput
            ) {
              entityCode
              entityType
              entityName
              entityAddress
              active
              enabledDispatch
              enabledRetire
              enabledPicking
              dispatchMaxReprograms
              enableMerchandiseCollect
              pickingSystemCode
              ubigeoCode
              managingEntities
            }
          }
        `,
        variables: {
          companyCode: onlyCompany ? session.user.entity.company.code : null,
          companyCodeInput: companyCodeInput,
          entityType: entityType,
          enabled: enabled,
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
        map(response => {
          if (!companyCodeInput) {
            session['entity' + entityType] = response;
            localStorage.setItem(
              environment.sessionAuth,
              JSON.stringify(session)
            );
          }
          return response;
        })
      )
    }
  }


  listEntityForReturns(type: string, enabled: boolean, companyCodeInput: string) {
    const entityType = type ? type : 'TD';
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (session && session['entity' + entityType]) {
      return of(session['entity' + entityType]);
    } else {
      return this.apollo
        .query({
          query: gql`
          query listEntities(
            $limit: Int
            $offset: Int
            $entityType: EntityType
            $enabled: Boolean,
            $companyCodeInput: String
          ) {
            listEntities(
              limit: $limit
              offset: $offset
              entityType: $entityType
              enabled: $enabled,
              companyCodeInput: $companyCodeInput
            ) {
              entityCode
              entityType
              entityName
              entityAddress
              active
              enabledDispatch
              enabledRetire
              enabledPicking
              dispatchMaxReprograms
              enableMerchandiseCollect
              pickingSystemCode
              ubigeoCode
              managingEntities
            }
          }
        `,
          variables: {
            companyCode: companyCodeInput,
            companyCodeInput: companyCodeInput,
            entityType: entityType,
            enabled: enabled,
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        }).pipe(
          map(response => {
            if (!companyCodeInput) {
              session['entity' + entityType] = response;
              localStorage.setItem(
                environment.sessionAuth,
                JSON.stringify(session)
              );
            }
            return response;
          })
        );
    }
  }

  listEntityMulticompany(type, enabled, companyCodeInput) {
    const entityType = type ? type : 'TD';
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (!companyCodeInput) {
      const userSession = session.user;
      companyCodeInput = userSession.entity.company.code
    }
    if (session && session['entity' + entityType + companyCodeInput]) {
      return of(session['entity' + entityType + companyCodeInput]);
    } else {
      return this.apollo
      .query({
        query: gql`
          query listEntities(
            $limit: Int
            $offset: Int
            $entityType: EntityType
            $enabled: Boolean,
            $companyCodeInput: String
          ) {
            listEntities(
              limit: $limit
              offset: $offset
              entityType: $entityType
              enabled: $enabled,
              companyCodeInput: $companyCodeInput
            ) {
              entityCode
              entityType
              entityName
              entityAddress
              active
              enabledDispatch
              enabledRetire
              enabledPicking
              dispatchMaxReprograms
              enableMerchandiseCollect
              pickingSystemCode
              ubigeoCode
              managingEntities
              pickingSystemCode
            }
          }
        `,
        variables: {
          companyCode: null,
          companyCodeInput: companyCodeInput,
          entityType: entityType,
          enabled: enabled,
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
        map(response => {
          session['entity' + entityType + companyCodeInput] = response;
          localStorage.setItem(
            environment.sessionAuth,
            JSON.stringify(session)
            );
          return response;
        })
      )
    }
  }

  listEntityByEntityCode(entityCode, enabled) {
    return this.apollo
    .query({
      query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $entityCode: String
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            entityCode: $entityCode
            enabled: $enabled
          ) {
            entityCode
            entityType
            entityName
            entityAddress
            active
            enabledDispatch
            ubigeoCode
            managingEntities,
            travelType
          }
        }
      `,
      variables: {
        entityCode: entityCode,
        enabled: enabled,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });
  }

  listEntityHasRetire(type, enabled,isRetire) {
    const entityType = type ? type : 'TD';
    return this.apollo
    .query({
      query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $entityType: EntityType
          $enabled: Boolean
          $isRetire: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            entityType: $entityType
            enabled: $enabled
            isRetire: $isRetire
          ) {
            entityCode
            entityType
            entityName
            entityAddress
            active
            enabledDispatch
            ubigeoCode
            managingEntities
          }
        }
      `,
      variables: {
        entityType: entityType,
        enabled: enabled,
        isRetire: isRetire,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });
  }

  /**
   * Función que retorna las entidades con en el estado de retiro en tienda
   * @param type tipo de entidad
   * @param enabled estado de la entidad
   */
  listEntityEnabledRetire(type, enabled) {
    const entityType = type ? type : 'TD';
    return this.apollo
    .query({
      query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $entityType: EntityType
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            entityType: $entityType
            enabled: $enabled
          ) {
            entityCode
            entityType
            entityName
            active
            enabledRetire
            managingEntities
          }
        }
      `,
      variables: {
        entityType: entityType,
        enabled: enabled
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });
  }

    /**
     * BORRAR: Función temporal
     */
    queryInformationEntityClasic(code) {
      return this.apollo
        .query({
          query: gql`
            query getEntities(
              $limit: Int
              $offset: Int
              $entityCode: String
              $enabled: Boolean
            ) {
              listEntities(
                limit: $limit
                offset: $offset
                entityCode: $entityCode
                enabled: $enabled
              ) {
                agoraStoreCode
                deliveryTime
                retreatZone
                ruc
                businessName
                latitude
                longitude
                contactNumber
                alwaysEntity
                entityCode
                entityType
                entityName
                entityAddress
                enabledPicking
                enabledDispatch
                enabledRetire
                enabledRetireOffline
                enabledRetireOnline
                pickingOrigins
                zoneSkus
                processingDays
                pickingDays
                pickingCutTime
                pickingSystemCode
                dispatchMaxReprograms
                ubigeoCode
                generalPickCapacity
                providerPickCapacity
                pickingCapacityDispatch
                pickingCutTimeDispatch
                pickingCapacityRetire
                pickingCutTimeRetire
                entityEmails
                enableMerchandiseCollect
                managingEntities
                isSellerDrop
                isExternalNetwork
                hierarchyCode
                ubigeo {
                  ubigeoCode
                  district
                  province
                  department
                }
                active
                productAttributes {
                  attributeCode
                  attributeType
                  attributeDescription
                  attributeBreadcrumb
                  parentAttributeCode
                  dispatchEnabled
                  retireEnabled
                  pickingEnabled
                }
                productHierarchy {
                  hierarchyCode
                  hierarchyName
                  hierarchyDescription
                  hierarchyBreadcrumb
                  hierarchyParent
                  dispatchEnabled
                  retireEnabled
                  pickingEnabled
                }
                inheritances {
                  entityCodeParent
                  entityNameParent
                  routeInheritance
                }
                entityZones {
                    zoneName
                    clientsZone
                    enabled
                    entityZoneHierarchies {
                      hierarchyCode
                      hierarchyName
                      hierarchyDescription
                      hierarchyBreadcrumb
                      hierarchyParent
                    }
                    hierarchies
                    groupPicking
                    zoneSku
                }
                agoraEntities { 
                  companyCode
                  entityCode
                  subsidiaryId
                  storeId
                  active
                  isSendAgora
                  isSendUpdateOnline
                  onlyToCentralizedStock
                  saleChannel
                }
                hierarchyCode
              }
            }
          `,
          variables: {
            entityCode: code,
            enabled: true
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        })
    }

  queryInformationEntity(code) {
    return this.apollo
    .query({
      query: gql`
        query getEntities(
          $limit: Int
          $offset: Int
          $entityCode: String
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            entityCode: $entityCode
            enabled: $enabled
          ) {
            additionalFields {
                componentType
                componentHeader
                fieldName
                fieldDescription
                fieldType
                companyCode
                fieldCode
                value
            }
            agoraStoreCode
            deliveryTime
            retreatZone
            ruc
            businessName
            latitude
            longitude
            contactNumber
            alwaysEntity
            serialNumber
            entityCode
            entityType
            entityName
            entityAddress
            enabledPicking
            enabledDispatch
            enabledRetire
            enabledRetireOffline
            enabledRetireOnline
            pickingOrigins
            zoneSkus
            processingDays
            pickingDays
            pickingCutTime
            pickingSystemCode
            dispatchMaxReprograms
            ubigeoCode
            clientsZoneSku
            vtexWarehouseId
            vtexDockId
            ignoreSundays
            ignoreHolidays
            generalPickCapacity
            providerPickCapacity
            pickingCapacityDispatch
            pickingCutTimeDispatch
            pickingCapacityRetire
            pickingCutTimeRetire
            entityEmails
            enableMerchandiseCollect
            managingEntities
            groupName
            isSellerDrop
            hierarchyCode
            wholesaleLeadTimeAdditionalDays
            isExternalNetwork
            ubigeo {
              ubigeoCode
              district
              province
              department
            }
            active
            productAttributes {
                attributeCode
                attributeType
                attributeDescription
                attributeBreadcrumb
                parentAttributeCode
                dispatchEnabled
                retireEnabled
                pickingEnabled
              }
            productHierarchy {
                hierarchyCode
                hierarchyName
                hierarchyDescription
                hierarchyBreadcrumb
                hierarchyParent
                dispatchEnabled
                retireEnabled
                pickingEnabled
              }
            entityCapacities {
              groupId
              category
              subCategory
              capacityLimit
              dispatchType
              parentAttributeCode
              reserveType
              entityType
              deliveryMode
              carrier
              }
            inheritances {
              entityCodeParent
              entityNameParent
              routeInheritance
            }
            originInheritances {
              entityCodeOriginParent
              entityNameOriginParent
              routeInheritance
            }
            entityZones {
                zoneName
                clientsZone
                enabled
                entityZoneHierarchies {
                  hierarchyCode
                  hierarchyName
                  hierarchyDescription
                  hierarchyBreadcrumb
                  hierarchyParent
                }
                hierarchies
                groupPicking
                zoneSku
            }
            agoraEntities { 
              companyCode
              entityCode
              subsidiaryId
              storeId
              active
              isSendAgora
              isSendUpdateOnline
              onlyToCentralizedStock
              saleChannel
            }
            travelType
          }
        }
      `,
      variables: {
        entityCode: code,
        enabled: true
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    })
    .pipe(map((response: any) => {
      let entity: Entity = response.data.listEntities[0];
      const newValue: any = {
        categoriasPickingCDRetire: [],
        categoriasPickingCDDispatch: []
      };
      if (entity && entity.entityCapacities && entity.entityCapacities.length) {
        entity.entityCapacities.forEach((item) => {
          const getCapacities = (acc: Entity, curr: Capacities, type): Capacities[] => {
            acc[type] = acc[type] ? acc[type] : [];
            acc[type].push({
              groupId: curr.groupId,
              category: curr.category,
              subCategory: curr.subCategory,
              capacity: curr.capacityLimit,
              dispatchType: curr.dispatchType,
              parentAttributeCode: curr.parentAttributeCode,
              carrier: curr.carrier ? curr.carrier.split('|') : []
            });
            return acc[type];
          }
          if (item.reserveType === 'picking') {
            switch (item.entityType) {
              case 'TD':
              case 'PRV':
                newValue.categoriasPickingTDPRV = getCapacities(newValue, item, 'categoriasPickingTDPRV');
                break;
              default:
                if (item.deliveryMode === SaleConfig.STORE_WITHDRAWAL) {
                  newValue['categoriasPickingCDRetire'] = getCapacities(newValue, item, 'categoriasPickingCDRetire');
                } else if (item.deliveryMode === SaleConfig.HOME_DELIVERY) {
                  newValue['categoriasPickingCDDispatch'] = getCapacities(newValue, item, 'categoriasPickingCDDispatch');
                }
                break;
            }
          }
          if (item.reserveType === 'dispatch') {
            switch (item.entityType) {
              case 'TD':
              case 'PRV':
                newValue['categoriasDispatchTDPRV'] = getCapacities(newValue, item, 'categoriasDispatchTDPRV');
                break;
              default:
                newValue['categoriasDispatchCDDispatch'] = getCapacities(newValue, item, 'categoriasDispatchCDDispatch');
                break;
            }
          }
        });
      }
      return { ...entity, ...newValue };
    }))
  }

    /**
   * Lista los sistemas de picking existentes
   */
  listPickingSystem() {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (session && session.pickingSystem) {
      return of(session.pickingSystem);
    } else {
      return this.apollo
      .query({
        query: gql`
          query listarPicking($limit: Int, $offset: Int) {
            listPickingSystem(limit: $limit, offset: $offset) {
              pickingSystemCode
              pickingSystemName
            }
          }
        `,
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      })
      .pipe(map(list => {
        session.pickingSystem = list;
        localStorage.setItem(
          environment.sessionAuth,
          JSON.stringify(session)
        );
        return list;
      }));
    }

  }

  /**
   * Actualiza la entidad
   * @param ManageEntityInput data actualizada
   */
  updateEntity(ManageEntityInput) {
    return this.apollo
    .mutate({
      mutation: gql`
        mutation createOrUpdateEntity($input: ManageEntityInput) {
          manageEntity(input: $input) {
            message
            data {
              entity {
                entityCode
                entityName
                entityType
                vtexWarehouseId
                vtexDockId
                ignoreSundays
                ignoreHolidays
                hierarchyCode
              }
              hierarchy {
                hierarchyCode
                retireEnabled
                dispatchEnabled
                pickingEnabled
              }
              attributes {
                attributeCode
                retireEnabled
                dispatchEnabled
                pickingEnabled
              }
              inheritances {
                entityCodeParent
                entityNameParent
                routeInheritance
              }
              originInheritances {
                entityCodeOriginParent
                entityNameOriginParent
                routeInheritance
              }
              entityZones {
                zoneName
                clientsZone
                enabled
                hierarchies
                groupPicking
                zoneSku
              }
              agoraEntities { 
                companyCode
                entityCode
                subsidiaryId
                storeId
                active
                isSendAgora
                isSendUpdateOnline
                onlyToCentralizedStock
                saleChannel
              }
            }
            error {
              name
              message
              details
            }
          }
        }
      `,
      variables: {
        input: ManageEntityInput
      }
    })
    .pipe();
  }

  /**
   * Función que retorna la lista de entidades que permiten retiro en tienda
   * @param enabled query
   */
async getEntitiesDispatch(enabled): Promise<Entity[]> {
  const session: Session = JSON.parse(
    localStorage.getItem(environment.sessionAuth)!
  );

  if (session && session.storesPicking) {
    return session.storesPicking;
  }

  const response: any = await firstValueFrom(
    this.apollo.query({
      query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $enabledRetire: Boolean
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            enabledRetire: $enabledRetire
            enabled: $enabled
          ) {
            entityCode
            entityType
            entityName
            shortName
            enabledRetire
            enabledPicking
            enabledDispatch
            ubigeoCode
            managingEntities
          }
        }
      `,
      variables: {
        /* enabledPicking: enabled.picking,
           enabledDispatch: enabled.dispatch, */
        enabledRetire: enabled.retire,
        enabled: true
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    })
  );

  const stores = response.data.listEntities;

  session.storesPicking = stores;

  localStorage.setItem(
    environment.sessionAuth,
    JSON.stringify(session)
  );
  return stores;
}

  listEntitiesPicking(enabled) {
    return this.apollo
    .query({
      query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $enabledPicking: Boolean,
          $entityType: EntityType,
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            enabledPicking: $enabledPicking
            entityType: $entityType
            enabled: $enabled
          ) {
            entityCode
            entityName
            entityType
            managingEntities
          }
        }
      `,
      variables: {
        enabledPicking: enabled.enabledPicking,
        entityType: enabled.type,
        enabled: enabled.enabled
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });
  }

  listEntityForType(enabled) {
    return this.apollo
    .query({
      query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $enabledRetire: Boolean,
          $enabledPicking: Boolean,
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            enabledRetire: $enabledRetire
            enabledPicking: $enabledPicking
            enabled: $enabled
          ) {
              entityCode
              entityType
              entityName
              entityAddress
              active
              enabledDispatch
              ubigeoCode
              enabledRetire
              enabledPicking
              managingEntities
          }
        }
      `,
      variables: {
        enabledRetire: enabled.enabledRetire,
        enabledPicking: enabled.enabledPicking,
        enabled: enabled.enabled
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    });
  }


  listEntityForType2(enabled, companyCodeInput) {
    return this.apollo
      .query({
        query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $enabledRetire: Boolean,
          $enabledPicking: Boolean,
          $enabled: Boolean
          $companyCodeInput: String
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            enabledRetire: $enabledRetire
            enabledPicking: $enabledPicking
            enabled: $enabled
            companyCodeInput: $companyCodeInput
          ) {
              entityCode
              entityType
              entityName
              entityAddress
              active
              enabledDispatch
              ubigeoCode
              enabledRetire
              enabledPicking
              managingEntities
          }
        }
      `,
        variables: {
          enabledRetire: enabled.enabledRetire,
          enabledPicking: enabled.enabledPicking,
          enabled: enabled.enabled,
          companyCodeInput: companyCodeInput
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      });
  }

  structuringData(myform: Entity, pickingDispatch: any, retire: any, pickingOrigins, extraDataForm, isCreate : boolean ,isCompanyWithNewConfig : boolean = false, entityZones : any[]) {
    const entityForm = myform;
    const entityExtraData = {
      deliveryTime: extraDataForm.deliveryTime,
      retreatZone: extraDataForm.retreatZone,
      latitude: extraDataForm.latitude ? extraDataForm.latitude.toString() : '',
      longitude: extraDataForm.longitude? extraDataForm.longitude.toString() : '',
      contactNumber: extraDataForm.contactNumber
    };

    const entity = {
      isCreate: isCreate,
      alwaysEntity: entityForm.alwaysEntity,
      entityCode: isCompanyWithNewConfig ? entityForm.entityCode : entityForm.entityCode.entityCode,
      entityType: entityForm.entityType,
      entityName: entityForm.entityName,
      entityAddress: entityForm.entityAddress,
      enabledPicking: entityForm.enabledPicking,
      enabledDispatch: entityForm.enabledDispatch,
      enabledRetire: entityForm.enabledRetire,
      processingDays: Number(entityForm.processingDays),
      pickingOrigins: pickingOrigins ? pickingOrigins : entityForm.pickingOrigins,
      pickingCutTime:
        entityForm.entityType === 'TD' || entityForm.entityType === 'PRV'
          ? entityForm.pickingCutTime
          : null,
      pickingSystemCode: entityForm.pickingSystemCode,
      dispatchMaxReprograms: Number(entityForm.dispatchMaxReprograms),
      pickingCapacityDispatch:
        entityForm.entityType === 'CD'
          ? Number(entityForm.pickingCapacityDispatch)
          : null,
      pickingCutTimeDispatch:
        entityForm.entityType === 'CD'
          ? entityForm.pickingCutTimeDispatch
          : null,
      pickingCapacityRetire:
        entityForm.entityType === 'CD'
          ? Number(entityForm.pickingCapacityRetire)
          : null,
      pickingCutTimeRetire:
        entityForm.entityType === 'CD' ? entityForm.pickingCutTimeRetire : null,
      generalPickCapacity:
        entityForm.entityType === 'TD'
          ? Number(entityForm.generalPickCapacity)
          : null,
      providerPickCapacity:
        entityForm.entityType === 'PRV'
          ? Number(entityForm.providerPickCapacity)
          : null,
      ubigeoCode: entityForm.ubigeoCode.ubigeoCode.toString(),
      active: entityForm.active,
      isBranch: entityForm.isBranch,
      parentEntityCode: entityForm.parentEntityCode,
      enableMerchandiseCollect: entityForm.enableMerchandiseCollect,
      entityEmails: this.addEmail(entityForm),
      zoneSkus: entityForm.zoneSkus,
    };

    const entityFinal = {...entity,...entityExtraData};

    const hierarchy: any[] = pickingDispatch.listHierarchy
      ? pickingDispatch.listHierarchy.concat(
        retire.listHierarchy ? retire.listHierarchy : []
      )
      : retire.listHierarchy
        ? retire.listHierarchy
        : [];
    const attributes = pickingDispatch.listAttributes
      ? pickingDispatch.listAttributes.concat(
        retire.listAttributes ? retire.listAttributes : []
      )
      : retire.listAttributes
        ? retire.listAttributes
        : [];
    return {
      entity: entityFinal,
      hierarchy: this.filterDuplicate(hierarchy, 'hierarchyCode'),
      attributes: this.filterDuplicate(attributes, 'attributeCode'),
      entityZones: this.cleanEntityZones(entityZones)
    };
  }

  cleanEntityZones(entityZones: any[]) {
    return (entityZones || []).map(item => {
      const { entityZoneHierarchies, __typename, ...rest } = item;
      return rest;
    });
  }

  addEmail(form) {
    const listEmails = [];
    for (const key in form) {
      if (!key.indexOf('email')) {
        listEmails.push(form[key]);
      }
    }
    return listEmails;
  }

  filterDuplicate(array: HierarchiesTem[], type: string) {
    array.map((item, i, list) => {
      list.map(data => {
        if (item[type] === data[type]) {
          item.dispatchEnabled = item.dispatchEnabled || data.dispatchEnabled ? true : false;
          item.pickingEnabled = item.pickingEnabled || data.pickingEnabled ? true : false;
          item.retireEnabled = item.retireEnabled || data.retireEnabled ? true : false;
          Object.assign(data, item);
        }
      });
    });
    return Array.from(new Set(array.map(item => item[type]))).map(code => {
      return array.find(data => data[type] === code);
    });
  }

  getDispatchCapacities(entityCode: string): Observable<Entity> {
    return this.apollo
    .query({
      query: gql`
        query getDispatchCapacities(
          $limit: Int
          $offset: Int
          $entityCode: String
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            entityCode: $entityCode
            enabled: $enabled
          ) {
            entityCode
            entityType
            entityName
            managingEntities
            configuredCapacities {
              dispatchTypeCode
              dispatchCapacity
              capacityStartTime
              capacityEndTime
            }
          }
        }
      `,
      variables: {
        entityCode: entityCode,
        enabled: true
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    })
    .pipe(map((response: any) => response.data.listEntities[0]));
  }

  createOrUpdateCapacities(ManageDispatchCapacityInput: any) {
    return this.apollo.mutate({
      mutation: gql`
        mutation createOrUpdateCapacities($input: ManageDispatchCapacityInput) {
          configureDispatchCapacity(input: $input) {
            message
            error {
              name
              message
              details
            }
          }
        }
      `,
      variables: {
        input: ManageDispatchCapacityInput
      }
     });
  }

  getLocalStores(): Entity[] {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    return session && session.stores ? session.stores : [];
  }

  async getStores(): Promise<Entity[]> {
      const session: Session = JSON.parse(
        localStorage.getItem(environment.sessionAuth)
      );
      if (session && session.stores) {
        return session.stores;
      } 
          const response: any = await firstValueFrom(
        this.apollo.query({
          query: gql`
            query getStores(
              $limit: Int
              $offset: Int
              $entityType: EntityType
              $enabled: Boolean
            ) {
              listEntities(
                limit: $limit
                offset: $offset
                entityType: $entityType
                enabled: $enabled
              ) {
                entityCode
                entityType
                entityName
                ubigeoCode
                managingEntities
                pickingSystemCode
              }
            }
          `,
          variables: {
                entityType: 'TD',
                enabled: true
              },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
            }),
          );

         const stores = response.data.listEntities;
          session.stores = stores;
          localStorage.setItem(
            environment.sessionAuth,
            JSON.stringify(session)
          );
          return stores;
  }

  getStoreOfUserAndSeller(): Observable<any[]> {
    const authUser: Session = JSON.parse(localStorage.getItem(environment.sessionAuth));
    let entities: any[] = [];
    let entityListFromAuthUser = [];

    if (authUser.user && authUser.user.entities) {
      entityListFromAuthUser = authUser.user.entities.map((entity: any) => {
        return {
          name: entity.entityName,
          id: entity.entityCode,
          entityType: entity.entityType
        };
      });
    } else {
      if (authUser.user && authUser.user.entity) {
        entityListFromAuthUser =
          [{
            name: authUser.user.entity.name,
            id: authUser.user.entity.entityCode,
            entityType: typeof authUser.user.entity.entityType === 'number' ? 'TD' : authUser.user.entity.entityType
            // *** Debería ser estandar
          }]
        ;
      }
    }

    return this.listEntity('SELLER', true, true).pipe(
      map((response: any) => {
        if (response && response.data && response.data.listEntities && response.data.listEntities[0]) {
          entities = response.data.listEntities;
          let sellerEntityList = entities.map((entity: any) => {
            return {
              name: entity.entityCode,
              id: entity.entityCode,
              entityType: entity.entityType
            };
          });

          // Concatenar ambos listados y ordenar por nombre
          const combinedEntityList = sellerEntityList.concat(entityListFromAuthUser)
            .sort((x, y) => x.name.toLowerCase() > y.name.toLowerCase() ? 1 : -1);

          return combinedEntityList;
        } else {
          // Si no hay entidades en la respuesta, devolver solo las entidades del usuario autenticado
          return entityListFromAuthUser;
        }
      })
    );
  }

  getStoreOfUser(): Observable<any[]> {
    const authUser: Session = JSON.parse(localStorage.getItem(environment.sessionAuth));
    if (authUser.user && authUser.user.entities) {
      return of(authUser.user.entities
        .map((entity: any) => {
          return {
            name: entity.entityName,
            id: entity.entityCode,
            entityType: entity.entityType
          };
        })
        .sort((x, y) => x.name.toLowerCase() > y.name.toLowerCase() ? 1 : -1));
    } else {
      if (authUser.user && authUser.user.entity) {
        return of(
          [{
            name: authUser.user.entity.name,
            id: authUser.user.entity.entityCode,
            entityType: typeof authUser.user.entity.entityType === 'number' ? 'TD' : authUser.user.entity.entityType
            // *** Debería ser estandar
          }]
        );
      }
    }
    return of([]);
  }

  getSaleEntities(): Observable<Entity[]>{
    const authUser: Session = JSON.parse(localStorage.getItem(environment.sessionAuth));

    const storesByType = authUser.stores;
    const userStores = authUser.user.entities;

    const filteredStores = [];

    userStores.forEach((userStore) => {
      const storeByTd = storesByType.find(i => i.entityCode == userStore.entityCode);

      if(storeByTd && storeByTd.pickingSystemCode == null){
        const updatedStore = {
          name: userStore.entityName,
          id: userStore.entityCode,
          entityType: userStore.entityType
        };
        filteredStores.push(updatedStore);
      }
    })

    return of(filteredStores);
  }

  /** Entidades que permiten picking */
async getEntitiesPicking(enabled: boolean): Promise<Entity[]> {
  const response = await firstValueFrom(
    this.apollo.query<{ listEntities: Entity[] }>({
      query: gql`
        query listEntities(
          $limit: Int
          $offset: Int
          $enabledPicking: Boolean
          $enabled: Boolean
        ) {
          listEntities(
            limit: $limit
            offset: $offset
            enabledPicking: $enabledPicking
            enabled: $enabled
          ) {
            entityCode
            entityType
            entityName
            shortName
            enabledRetire
            enabledPicking
            enabledDispatch
            ubigeoCode
            pickingSystemCode
            managingEntities
          }
        }
      `,
      variables: {
        enabledPicking: enabled,
        enabled: true
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    })
  );

  return response.data.listEntities;
}


  listEasternEntities(type, enabledEast): Observable<any> {
    const entityType = type ? type : 'TD';
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (session && session['entityEas' + entityType]) {
      return of(session['entityEas' + entityType]);
    } else {
      return this.apollo
      .query({
        query: gql`
          query listEasternEntities(
            $limit: Int
            $offset: Int
            $entityType: EntityType
            $enabledEast: Boolean
          ) {
            listEasternEntities(
              limit: $limit
              offset: $offset
              entityType: $entityType
              enabledEast: $enabledEast
            ) {
              entityCode
              entityType
              entityName
              active
              enabledDispatch
            }
          }
        `,
          variables: {
            entityType: entityType,
            enabledEast: enabledEast
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        })
        .pipe(map((response: any) => {
          const value = response.data.listEasternEntities && response.data.listEasternEntities.length ? response.data.listEasternEntities : [];
          session['entityEas' + entityType] = value;
          localStorage.setItem(
            environment.sessionAuth,
            JSON.stringify(session)
          );
          return value;
        }));
    }
  }

  getEntityAdditionalData(code) {
    return this.apollo
      .query({
        query: gql`
          query getEntities(
            $limit: Int
            $offset: Int
            $entityCode: String
            $enabled: Boolean
            $fields: [String]
          ) {
            listEntities(
              limit: $limit
              offset: $offset
              entityCode: $entityCode
              enabled: $enabled
              fields: $fields
            ) {
              processingDays
              pickingSystemCode
              enabledPicking
            }
          }
      `,
        variables: {
          entityCode: code,
          enabled: true,
          fields: ['processingDays', 'pickingSystemCode', 'enabledPicking']
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      })
      .pipe(map((response: any) => {
        let entityResponse: Entity = response.data.listEntities[0];
        if (!entityResponse) {
          return null;
        }
        const entity: Entity = {
          ...entityResponse,
          processingDays: entityResponse.processingDays ? entityResponse.processingDays : 0,
          pickingSystemCode: entityResponse.pickingSystemCode,
          enabledPicking: entityResponse.enabledPicking ? entityResponse.enabledPicking : false
        };
        return entity;
      }));
  }

  /**
  * Actualiza ciertos campos de la entidad
  * @param updateEntityFieldsInput data actualizada
  */
  updateEntityFields(updateEntityFieldsInput) {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation updateEntityFields($input: UpdateEntityFieldsInput) {
            updateEntityFields(input: $input) {
              message
              data {
                entityCode
                processingDays
                pickingSystemCode
                enabledPicking
                pickingDays
                pickingCutTimeDispatch
                pickingCutTimeRetire
              }
              error {
                name
                message
                details
              }
            }
          }
      `,
        variables: {
          input: updateEntityFieldsInput
        }      })
      .pipe();
  }
}
